import React from 'react';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner } from '../../design';
import { LineChart, BarChart, ChartLegend, ChartLegendStrip, useChartLegend } from '../charts';
import { useAuthStore } from '../../../../store/authStore';
import type { ThemeMode } from '../../../../types';
import type { ChartSpec } from './chartWidgetTypes';

export interface ChartWidgetProps {
  /** Fetch state — usually straight from useDataProviderWidget. */
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Dim + show the preview banner when no asset is bound. */
  isPreview: boolean;
  /** The chart description for this render. */
  spec: ChartSpec;
  /** Optional header bar above the chart (price/return readout, etc.). */
  header?: React.ReactNode;
  /** Optional toolbar above the chart (range buttons, series selectors). */
  toolbar?: React.ReactNode;
  /** Extra classes on the chart area wrapper. Defaults to the uniform card
   *  padding — pass '' to opt out. */
  chartPadClassName?: string;
  /** Classes for an inner surface div around the chart. Defaults to the
   *  uniform tinted surface — pass '' to opt out. */
  chartSurfaceClassName?: string;
  /** Replaces the chart area entirely when there are no rows (e.g. a
   *  missing-credentials notice). Takes precedence over spec.emptyMessage. */
  emptyContent?: React.ReactNode;
  /** Classes for the outer container. Defaults to the uniform bordered card —
   *  every chart widget gets the same chrome unless it opts out. */
  containerClassName?: string;
}

/**
 * Shared shell for chart-shaped widgets. Owns the bits every chart widget used
 * to re-implement by hand: loading/error/preview states, theme acquisition,
 * legend visibility state, the floating ChartLegend, and rendering the right
 * chart block (LineChart/BarChart) from a ChartSpec. The per-type renderer
 * supplies the fetch result, the header/toolbar slots, and the spec.
 */
const ChartWidget: React.FC<ChartWidgetProps> = ({
  loading,
  error,
  refetch,
  isPreview,
  spec,
  header,
  toolbar,
  // Uniform card chrome: every chart widget renders inside the same bordered,
  // tinted container — no per-widget snowflakes (one boxed chart next to a
  // flush one reads broken).
  chartPadClassName = 'px-3 pt-3 pb-3',
  chartSurfaceClassName = 'h-full rounded-xl bg-neutral-50/50',
  emptyContent,
  containerClassName = 'rounded-xl border border-neutral-200 bg-surface shadow-sm',
}) => {
  const themeMode: ThemeMode = useAuthStore((s) => s.user?.theme) ?? 'light';
  const { hiddenKeys, toggle, toggleAll } = useChartLegend();

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;

  const hasData = spec.rows.length > 0;
  const allKeys = (spec.legendSeries ?? []).map((s) => s.key);
  const hasLegend = !!spec.legendSeries && spec.legendSeries.length > 0 && hasData;
  // Strip is the chart-DS default; 'floating' is the opt-in draggable panel.
  const legendVariant = spec.legendVariant ?? 'strip';
  const showStrip = hasLegend && legendVariant !== 'floating';
  const showFloating = hasLegend && legendVariant === 'floating';

  const chart = spec.chartKind === 'bar' ? (
    <BarChart
      data={spec.rows}
      xKey={spec.xKey}
      series={spec.barSeries}
      overlays={spec.overlays}
      legend={false}
      hiddenSeries={hiddenKeys}
      showEndValueTags={spec.showEndValueTags}
      endValueFormatter={spec.endValueFormatter}
      xLabelFormatter={spec.xLabelFormatter}
      yLabelFormatter={spec.yLabelFormatter}
      yLabelFormatters={spec.yLabelFormatters}
      xTitle={spec.xTitle}
      yTitles={spec.yTitles}
      theme={themeMode}
    />
  ) : (
    <LineChart
      data={spec.rows}
      xKey={spec.xKey}
      series={spec.lineSeries}
      legend={false}
      hiddenSeries={hiddenKeys}
      enableCrosshair={spec.enableCrosshair}
      hoverSeriesKey={spec.hoverSeriesKey}
      onDatumHover={spec.onDatumHover}
      showEndValueTags={spec.showEndValueTags}
      endValueFormatter={spec.endValueFormatter}
      xLabelFormatter={spec.xLabelFormatter}
      xTickValues={spec.xTickValues}
      xTitle={spec.xTitle}
      yTitles={spec.yTitles}
      axisColors={spec.yAxisColors}
      zeroLineAxes={spec.zeroLineAxes}
      yLabelFormatter={spec.yLabelFormatter}
      yLabelFormatters={spec.yLabelFormatters}
      yTickCounts={spec.yTickCounts}
      alignZeroAxes={spec.alignZeroAxes}
      theme={themeMode}
    />
  );

  const chartArea = emptyContent && !hasData
    ? emptyContent
    : hasData
      ? chart
      : (
        <div className="flex items-center justify-center h-full text-sm text-neutral-500">
          {spec.emptyMessage ?? 'No data available'}
        </div>
      );

  return (
    <div className={`h-full flex flex-col overflow-hidden ${containerClassName} ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      {header}
      {toolbar}
      <div className={`flex-1 min-h-0 relative overflow-hidden ${chartPadClassName}`}>
        {chartSurfaceClassName ? (
          <div className={chartSurfaceClassName}>{chartArea}</div>
        ) : (
          chartArea
        )}
        {showFloating && (
          <div className="absolute top-2 left-2 z-10">
            <ChartLegend
              series={spec.legendSeries!}
              hiddenKeys={hiddenKeys}
              onToggle={toggle}
              onToggleAll={(show) => toggleAll(show, allKeys)}
              defaultCollapsed={!spec.legendStartExpanded}
            />
          </div>
        )}
      </div>
      {(showStrip || spec.footnote) && (
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-3 pb-2 pt-1">
          {showStrip ? (
            <ChartLegendStrip series={spec.legendSeries!} hiddenKeys={hiddenKeys} onToggle={toggle} />
          ) : (
            <span />
          )}
          {spec.footnote && (
            <span className="text-[9px] uppercase tracking-wide text-neutral-400">{spec.footnote}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ChartWidget;
